import { combineLatest, filter, Observable, switchMap, tap } from 'rxjs';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ID } from '@datorama/akita';
import { MatDialog } from '@angular/material/dialog';

import { EntitiesService, Entity, EntityMember } from '@state/entities';
import { EntityService } from '@state/entity';
import { FormValues, FormWizardQuery, NextEvent } from '@state/form-wizard';
import { UserQuery } from '@state/user';
import { WorkflowQuery } from '@state/workflow';

import { CustomVariable, RenderedFormService } from '../rendered-form.service';
import {
  InvestmentProfileSelectionWarningModalComponent,
  InvestmentProfileSelectionWarningModalComponentData
} from './investment-profile-selection-warning-modal';

interface EntityWithRelations extends Entity {
  hasUser: boolean;
  hasInvestor: boolean;
}
@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fl-investment-profile-selector',
  styleUrls: ['./investment-profile-selector.component.scss'],
  templateUrl: './investment-profile-selector.component.html'
})
export class InvestmentProfileSelectorComponent implements OnInit {
  @Input() vehicle: { id: ID; entity_id: ID };
  @Output() flNext: EventEmitter<NextEvent> = new EventEmitter<NextEvent>();

  entities$: Observable<Entity[]>;
  investorDetails: { email: string; id: ID; name: string };
  investorDetails$ = this.workflowQuery.investorDetails$;
  isSubmitting$ = this.formWizardQuery.isSubmitting$;
  loaded = false;
  selectableEntities: EntityWithRelations[] = [];
  /**
   * In scenarios where the user is not the investment's investor, we split the entities based
   * on whether the user (e.g. a RIA) and/or the investor is already a member.
   * These are defined as member variables for the template as opposed to getters doing the filtering
   * to avoid needless work on change detection cycles.
   */
  selectableEntitiesWithUserAndInvestor: Entity[] = [];
  selectableEntitiesWithUserButWithoutInvestor: Entity[] = [];
  selectedEntity: Entity;
  user$ = this.userQuery.select('user');
  investorMatchesCurrentUser: boolean;

  constructor(
    private dialog: MatDialog,
    private entitiesService: EntitiesService,
    private entityService: EntityService,
    private formWizardQuery: FormWizardQuery,
    private renderedFormService: RenderedFormService,
    private userQuery: UserQuery,
    private workflowQuery: WorkflowQuery
  ) {}

  ngOnInit() {
    this.investorMatchesCurrentUser = this.workflowQuery.investorMatchesCurrentUser();

    combineLatest([this.workflowQuery.investorDetails$, this.entitiesService.getAndStoreEntities$()])
      .pipe(
        tap({
          next: ([investorDetails, entities]) => {
            this.investorDetails = investorDetails;
            this.loaded = true;
            this.setEntities(entities);
          }
        }),
        filter(
          () =>
            this.renderedFormService.dynamicModels[CustomVariable.InvestmentAccount] && !this.selectedEntity
        ),
        switchMap(() => {
          let entityId: ID = this.renderedFormService.dynamicModels[CustomVariable.InvestmentAccount];
          return this.entityService.getAndStoreEntity$(entityId);
        }),
        tap({ next: (entity) => (this.selectedEntity = entity) })
      )
      .subscribe();
  }

  get showEntitySelector(): boolean {
    return this.selectedEntity ? this.selectableEntities.length > 1 : !!this.selectableEntities.length;
  }

  createNewInvestmentProfile(): void {
    this.renderedFormService.dynamicModels = {};
    this.renderedFormService.dynamicModels[CustomVariable.InvestmentAccount] = 'new';
    this.renderedFormService.dynamicModels[CustomVariable.InvestmentAccountShowSummary] = 'false';

    let initialForm: FormValues = { ...this.renderedFormService.dynamicModels };

    setTimeout(() => {
      this.flNext.emit({ initialForm, showProgress: true });
    });
  }

  isProfileSelected(investmentProfile: Entity): boolean {
    return this.renderedFormService.dynamicModels[CustomVariable.InvestmentAccount] === investmentProfile.id;
  }

  selectInvestmentProfile(investmentProfile: Entity): void {
    // clear form so backend will only return what's selected
    let dynamicModels = this.renderedFormService.dynamicModels;
    for (let value in dynamicModels) {
      if (this.renderedFormService.dynamicModels.hasOwnProperty(value)) {
        if (value !== CustomVariable.InvestmentProviderAccessRequired) delete dynamicModels[value];
      }
    }

    dynamicModels[CustomVariable.InvestmentAccount] = investmentProfile.id;

    // send along the investor ID to automatically add them to the selected profile
    if (!this.investorMatchesCurrentUser) {
      dynamicModels[CustomVariable.InternalAddMemberEmail] = this.investorDetails.email;
    }

    if (!investmentProfile.draft) dynamicModels[CustomVariable.InvestmentAccountShowSummary] = 'true';

    setTimeout(() => {
      this.flNext.emit({ showProgress: true });
    });
  }

  onSelectInvestmentProfile(investmentProfile: Entity): void {
    if (this.investorMatchesCurrentUser) {
      this.selectInvestmentProfile(investmentProfile);
      return;
    }

    // Handle the case where a user is acting on behalf of the investor.
    // Should show a warning modal if the selected profile does not contain the investor and/or
    // has additional members beyond the investor and user
    let showConfirmationModal = false;

    let profileHasUserButNotInvestor = this.selectableEntitiesWithUserButWithoutInvestor.some(
      (entity) => entity.id === investmentProfile.id
    );

    if (profileHasUserButNotInvestor) showConfirmationModal = true;

    // want to warn in the event of extra members, which would then have access to the investment data
    let extraMembers = this.filterEntityMembers({
      entityMembers: investmentProfile.investment_account?.entity?.entity_members,
      memberIdsToExclude: [this.investorDetails?.id, this.userQuery.getValue()?.user?.id]
    });

    if (extraMembers.length) {
      showConfirmationModal = true;
    }

    if (!showConfirmationModal) {
      this.selectInvestmentProfile(investmentProfile);
      return;
    }

    let modalData: InvestmentProfileSelectionWarningModalComponentData = {
      entity: investmentProfile,
      investorDetails: this.investorDetails,
      profileHasUserButNotInvestor
    };

    this.dialog
      .open(InvestmentProfileSelectionWarningModalComponent, { data: modalData })
      .afterClosed()
      .subscribe({
        next: (confirmed: boolean) => {
          if (confirmed) this.selectInvestmentProfile(investmentProfile);
        }
      });
  }

  /**
   * Returns entity members that are not in the `memberIdsToExclude` list
   * @param entityMembers
   * @param memberIdsToExclude
   * @returns
   */
  filterEntityMembers({
    entityMembers,
    memberIdsToExclude
  }: {
    entityMembers: EntityMember[];
    memberIdsToExclude: ID[];
  }): EntityMember[] {
    if (!entityMembers?.length) return [];
    if (!memberIdsToExclude?.length) return entityMembers;

    let exclude: Set<ID> = new Set(memberIdsToExclude);
    return entityMembers.filter((member) => !exclude.has(member.user_id));
  }

  /**
   * Populates the relevant entity state used by the template
   * Sets the selected entity if there is a match
   * @param entities
   */
  setEntities(entities: Entity[]): void {
    let { id: investorId } = this.investorDetails;
    let userId = this.userQuery.getValue().user.id;

    // tag each entity based on whether the user and/or investor is present
    let parsedEntities = entities.map((entity) => {
      let entityMemberUserIds = entity.investment_account?.entity?.entity_members?.map((em) => em.user_id);

      if (!entityMemberUserIds) {
        return {
          ...entity,
          hasInvestor: null,
          hasUser: null
        };
      }

      let entityHasUserMember = entityMemberUserIds.some((id) => id === userId);
      let entityHasInvestorMember = entityMemberUserIds.some((id) => id === investorId);

      return {
        ...entity,
        hasInvestor: entityHasInvestorMember,
        hasUser: entityHasUserMember
      };
    });

    // actually set the member variables used by the template
    for (let entity of parsedEntities) {
      if (this.isProfileSelected(entity)) this.selectedEntity = entity;
      this.selectableEntities.push(entity);

      if (entity.hasInvestor && entity.hasUser) {
        this.selectableEntitiesWithUserAndInvestor.push(entity);
      } else if (entity.hasUser) {
        this.selectableEntitiesWithUserButWithoutInvestor.push(entity);
      }
    }
  }
}
